import { useQuery } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { saveScheduledInstall, scheduleFormQuery, type ScheduleFormPage } from '../api/softseeWrites'
import { ApiError } from '../api/client'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { DatePicker, TimePicker } from '../ui/DatePicker'
import { Spinner } from '../ui/Spinner'
import { schedulePayload } from './softseeWriteForm'
import { SettingsCheck } from '../ui/SettingsParts'
import './ScheduleInstallForm.css'

const WEEKDAYS = [
  [1, 'Monday'],
  [2, 'Tuesday'],
  [3, 'Wednesday'],
  [4, 'Thursday'],
  [5, 'Friday'],
  [6, 'Saturday'],
  [7, 'Sunday'],
] as const

function plusOneHour(): { date: string; time: string } {
  const when = new Date(Date.now() + 60 * 60 * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    date: `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`,
    time: `${pad(when.getHours())}:${pad(when.getMinutes())}`,
  }
}

/**
 * Softsee::scheduleInstallForm + saveScheduledInstall. Aceleasi campuri ca
 * scheduleInstall.php: devices[], package_id, date+time lipite in scheduled_at
 * / deadline_at, recurrence 0 cand bifa e goala.
 */
export function ScheduleInstallForm({ chosen = [], onlyDevice = 0, onSaved }: {
  chosen?: number[]
  onlyDevice?: number
  onSaved?(): void
}) {
  const page = useQuery(scheduleFormQuery(chosen, onlyDevice))
  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return <ScheduleBody page={page.data} onSaved={onSaved} />
}

function ScheduleBody({ page, onSaved }: { page: ScheduleFormPage; onSaved?(): void }) {
  /* Ora de pornire se socoteste O DATA, la deschidere - ca `date('H:i', ...)`
     din macheta, care se scria si el o singura data. Chemata la fiecare
     desenare, ar fi mutat ora sub degetul omului la orice bifa. */
  const [start] = useState(plusOneHour)
  const [scheduledDate, setScheduledDate] = useState(start.date)
  const [scheduledTime, setScheduledTime] = useState(start.time)
  const [deadlineDate, setDeadlineDate] = useState('')
  const [deadlineTime, setDeadlineTime] = useState('')
  const [repeat, setRepeat] = useState(false)
  const [recurrenceTime, setRecurrenceTime] = useState('22:00')
  const [kind, setKind] = useState('1')
  const [busy, setBusy] = useState(false)
  const chosen = new Set(page.chosen)
  /* Cele doua pickere sunt controlate, dar <select>-urile lor raman in pagina
     (ascunse, ca la bootstrap-select), deci `schedulePayload` citeste mai
     departe aceleasi campuri: `devices[]` din optiunile alese si `package_id`
     din valoare. */
  const [devices, setDevices] = useState<string[]>(() =>
    page.devices.filter((d) => chosen.has(d.ID)).map((d) => String(d.ID)),
  )
  const [pack, setPack] = useState('')

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const body = await saveScheduledInstall(schedulePayload(e.currentTarget))
      if (body.status !== 'OK') {
        toast(body.message || 'Not scheduled', 'Error', 'error')
        return
      }
      toast(body.message || 'Scheduled', 'Scheduled', 'success')
      onSaved?.()
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Not scheduled', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {page.locked ? null : (
        <h4 style={{ margin: '0 0 14px' }}>
          <strong>Schedule</strong> package installation
        </h4>
      )}
      {/*
          `noValidate`: JUDECATA RAMANE A SERVERULUI, CA IN PORTAL.

          `active_days` e `type="number"` cu `min`/`max`: o valoare in afara lor
          opreste TOT formularul. Cand campul se vede, browserul arata un balon
          si e in regula - dar campul ala sta ascuns cat timp Recurrence nu e
          bifat, iar pe un camp ascuns Chrome refuza trimiterea fara sa spuna
          nimic omului, doar cu un rand in consola. Adica exact felul de buton
          care "nu face nimic" pe care il reparam aici.

          In macheta veche nu exista nicio verificare in browser: pleca ce scria
          in casuta, iar `SoftseeWrites::saveScheduled` raspundea "The job needs
          a date and time." Asa ramane.
      */}
      <form id="scheduleInstallForm" action="/softsee/saveScheduledInstall" method="post" noValidate onSubmit={onSubmit}>
        <input type="hidden" name="company_id" value={page.company_id} />

        <div className="si-row">
          <label htmlFor="siDevices">Devices</label>
          {page.locked ? (
            page.devices.map((device) => (
              <div key={device.ID}>
                <input type="hidden" name="devices[]" value={device.ID} />
                <p style={{ margin: 0, fontWeight: 600 }}>
                  <i className="fas fa-desktop" style={{ color: '#98a1aa' }}></i> {device.Denumire}
                </p>
                <span className="si-help">Only this device.</span>
              </div>
            ))
          ) : (
            <>
              {/* `data-live-search` + `data-actions-box` + `count > 2`, ca in
                  scheduleInstall.php. "Select All" alege ce a ramas dupa
                  cautare, nu toata firma - vezi SelectPicker. */}
              <SelectPicker
                id="siDevices"
                name="devices[]"
                className="form-control"
                multiple
                actionsBox
                selectedTextFormat="count > 2"
                size={8}
                title="Nothing selected"
                options={page.devices.map((device) => ({ value: String(device.ID), label: device.Denumire }))}
                value={devices}
                onChange={setDevices}
              />
              <span className="si-help">
                {page.chosen.length} checked in the list, out of {page.devices.length}. Use <strong>Select All</strong> in
                the dropdown for every device.
              </span>
            </>
          )}
        </div>

        <div className="si-row">
          <label htmlFor="siPackage">Package</label>
          {/* Si el era `selectpicker` cu `data-live-search`: lista de pachete a
              unei firme e lunga, si se cauta dupa nume. */}
          <SelectPicker
            id="siPackage"
            name="package_id"
            className="form-control"
            size={8}
            title="Choose a package…"
            options={page.packages.map((p) => ({ value: String(p.ID), label: p.Name }))}
            value={pack}
            onChange={setPack}
          />
          <span className="si-help">Packages are built under Settings › Softsee › Manage Custom Packages.</span>
        </div>

        {/*
            CELE PATRU CASUTE DE TIMP.

            In macheta veche erau `<input type="text">` cu clasele `datepicker`
            si `timepicker`, iar jQuery le imbraca la incarcare (bootstrap-
            datepicker cu `format: 'yyyy-mm-dd'`, wickedpicker cu
            `twentyFour: true`). Aici nu ruleaza jQuery, deci clasele alea nu
            chemau pe nimeni: ramaneau patru casute albe de text din care nu se
            deschidea nimic - iar la Deadline, unde nu era nici macar o valoare
            scrisa dinainte, nu se vedea ca ele fac ceva. Mai rau, `.datepicker`
            din app.css e foaia DROPDOWN-ului bootstrap: pusa pe casuta, ii
            strangea textul de margine (padding 4px in loc de 6px 12px).

            Ce PLEACA la server nu se schimba: `scheduled_date` / `deadline_date`
            raman `yyyy-mm-dd`, orele raman `HH:mm`, si `schedulePayload` le
            lipeste mai departe in `scheduled_at` si `deadline_at`.
        */}
        <div className="si-row">
          <label htmlFor="siScheduledDate">Start at</label>
          <div className="si-inline" style={{ width: 170 }}>
            <DatePicker id="siScheduledDate" name="scheduled_date" value={scheduledDate} onChange={setScheduledDate} typed icon="far fa-calendar-alt ico" />
          </div>
          <div className="si-inline" style={{ width: 120 }}>
            <TimePicker id="siScheduledTime" name="scheduled_time" value={scheduledTime} onChange={setScheduledTime} />
          </div>
          <span className="si-help">A device that is off will pick the job up when it comes online.</span>
        </div>

        <div className="si-row">
          <label htmlFor="siDeadlineDate">
            Deadline <span style={{ fontWeight: 400, color: '#98a1aa' }}>(optional)</span>
          </label>
          <div className="si-inline" style={{ width: 170 }}>
            <DatePicker id="siDeadlineDate" name="deadline_date" value={deadlineDate} onChange={setDeadlineDate} typed placeholder="no deadline" icon="far fa-calendar-alt ico" />
          </div>
          <div className="si-inline" style={{ width: 120 }}>
            <TimePicker id="siDeadlineTime" name="deadline_time" value={deadlineTime} onChange={setDeadlineTime} />
          </div>
          {/* Textul vine din politica de restart a firmei (Settings > Softsee >
              Restarts). Pana pe 12 octombrie 2026 aici scria ca fara termen
              agentul il roaga pe om sa reporneasca - si nu exista nicio
              rugaminte (auditul CEC, cerinta 60). */}
          <span className="si-help" data-testid="si-restart-help">
            {page.restart ? (
              <>
                {page.restart.with_deadline} {page.restart.without_deadline}{' '}
                <span style={{ color: '#98a1aa' }}>{page.restart.older_agents}</span>
              </>
            ) : (
              'With a deadline, the agent schedules a restart for the deadline once the package is installed.'
            )}
          </span>
        </div>

        <div className="si-row">
          <SettingsCheck id="siRepeat" name="repeat" value="1" checked={repeat} onChange={setRepeat} label="Recurrence" />
          <div className="si-recurrence">
            <div className="si-inline si-repeat-only" style={{ width: 150, display: repeat ? undefined : 'none' }}>
              <label htmlFor="siRecurrenceKind">How often</label>
              <select className="form-control input-sm" id="siRecurrenceKind" name="recurrence" value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="1">Every day</option>
                <option value="2">Every week</option>
                <option value="3">Every month</option>
              </select>
            </div>
            <div className="si-inline si-repeat-only" id="siWeekday" style={{ width: 150, display: repeat && kind === '2' ? undefined : 'none' }}>
              <label htmlFor="siDayOfWeek">On</label>
              <select className="form-control input-sm" id="siDayOfWeek" name="recurrence_day_week" defaultValue="1">
                {WEEKDAYS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-inline si-repeat-only" id="siMonthday" style={{ width: 150, display: repeat && kind === '3' ? undefined : 'none' }}>
              <label htmlFor="siDayOfMonth">Day of the month</label>
              <select className="form-control input-sm" id="siDayOfMonth" name="recurrence_day_month" defaultValue="1">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-inline si-repeat-only" style={{ width: 120, display: repeat ? undefined : 'none' }}>
              <label htmlFor="siTime">At</label>
              {/* A treia casuta de ora din panou, cu aceeasi clasa moarta
                  `timepicker`. Nu se plangea nimeni de ea fiindca se vede abia
                  dupa ce se bifeaza Recurrence - dar e aceeasi casuta stinsa. */}
              <TimePicker id="siTime" name="recurrence_time" value={recurrenceTime} onChange={setRecurrenceTime} className="input-sm" />
            </div>
            <div className="si-inline si-repeat-only" style={{ width: 170, display: repeat ? undefined : 'none' }}>
              <label htmlFor="siActiveDays">
                Stays alive <i className="fas fa-info-circle si-info" data-toggle="tooltip" data-placement="top" title="After that, a job a device never picked up is written off, so an old package does not land on a machine that has been off for weeks."></i>
              </label>
              <div>
                <input type="number" className="form-control input-sm si-inline" id="siActiveDays" name="active_days" min={1} max={365} defaultValue={7} style={{ width: 80 }} />
                <span style={{ lineHeight: '32px' }}> days</span>
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary" id="siSave" disabled={busy}>
          <i className="fas fa-clock"></i> Schedule
        </button>
      </form>
    </>
  )
}
